import React from 'react';
import { cn } from '@/shared/utils/cn';
import { useDateTimeInput } from '../hooks/useDateTimeInput';
import { sanitizeNumericSegment } from '../utils/datetimeFormat';
import {
  DateTimeSeparator,
  MeridiemToggleStack,
  SegmentField,
  TimePickerColon,
  TimeSegmentBox,
} from './DateTimeSegmentFields';

export interface TimePickerPanelProps {
  /** Fecha base (día) sobre la que se aplica la hora. */
  value: Date | null;
  showSeconds?: boolean;
  /** Si es true, los segmentos y AM/PM no son editables. */
  disabled?: boolean;
  onTimeChange?: (date: Date | null) => void;
  className?: string;
}

/**
 * Panel de selección de hora para el popup del DatePicker (formato 12h).
 *
 * Reutiliza {@link SegmentField} y estilos compartidos con {@link DateTimeSegmentInput}.
 * Muestra cajas numéricas para hora, minutos y segundos opcionales, más toggle AM/PM vertical.
 *
 * @example
 * ```tsx
 * <TimePickerPanel
 *   value={selectedDate}
 *   showSeconds
 *   onTimeChange={setSelectedDate}
 * />
 * ```
 */
export const TimePickerPanel: React.FC<TimePickerPanelProps> = ({
  value,
  showSeconds = true,
  disabled = false,
  onTimeChange,
  className,
}) => {
  const { parts, updatePart, commitPart, setMeridiem } = useDateTimeInput({
    value,
    showSeconds,
    onChange: disabled ? undefined : onTimeChange,
  });

  const formatLabel = showSeconds ? '(hh:mm:ss)' : '(hh:mm)';

  return (
    <div className={cn('ui-border-t ui-border-default', className)}>
      <div className="ui-flex ui-flex-col ui-gap-8 ui-p-16">
        <p className="ui-flex ui-items-center ui-gap-4 ui-text-body-xs-medium ui-font-body-xs-medium ui-leading-body-xs-medium ui-tracking-body-xs ui-text-primary">
          Elige hora
          <span className="ui-text-tertiary">{formatLabel}</span>
        </p>

        <div className="ui-flex ui-items-center ui-gap-8">
          <div className="ui-flex ui-items-center ui-gap-4">
            <TimeSegmentBox>
              <SegmentField
                boxed
                disabled={disabled}
                value={parts.hour12}
                placeholder="hh"
                maxLength={2}
                ariaLabel="Hora (01-12)"
                onChange={(v) => updatePart('hour12', sanitizeNumericSegment(v))}
                onCommit={() => commitPart('hour12')}
              />
            </TimeSegmentBox>
            <TimePickerColon />
            <TimeSegmentBox>
              <SegmentField
                boxed
                disabled={disabled}
                value={parts.minute}
                placeholder="mm"
                maxLength={2}
                ariaLabel="Minutos (00-59)"
                onChange={(v) => updatePart('minute', sanitizeNumericSegment(v))}
                onCommit={() => commitPart('minute')}
              />
            </TimeSegmentBox>
            {showSeconds && (
              <>
                <TimePickerColon />
                <TimeSegmentBox>
                  <SegmentField
                    boxed
                    disabled={disabled}
                    value={parts.second}
                    placeholder="ss"
                    maxLength={2}
                    ariaLabel="Segundos (00-59)"
                    onChange={(v) => updatePart('second', sanitizeNumericSegment(v))}
                    onCommit={() => commitPart('second')}
                  />
                </TimeSegmentBox>
              </>
            )}
          </div>

          <MeridiemToggleStack value={parts.meridiem} disabled={disabled} onSelect={setMeridiem} />
        </div>
      </div>
    </div>
  );
};
